import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Owner tool: stamp 3-months-from-purchase expiry onto already-sold packs
// that currently have no expiry. Preview (dry-run) first, then Apply.
export default function PackExpiryBackfillCard() {
    const [preview, setPreview] = useState(null); // null = not previewed
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [done, setDone] = useState(null);

    const loadPreview = async () => {
        setLoading(true);
        setDone(null);
        try {
            const r = await axios.get(`${API}/coach/packs/backfill-expiry/preview`, { withCredentials: true });
            setPreview(r.data);
        } catch (e) {
            toast.error("Couldn't load preview", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    };

    const apply = async () => {
        if (!window.confirm(
            `Apply a 3-month-from-purchase expiry to ${preview.total} pack(s)?\n\n` +
            `${preview.would_expire_now} were bought over 3 months ago and will become EXPIRED immediately ` +
            `(their remaining sessions stop being usable).\n\nThis cannot be auto-undone.`
        )) return;
        setApplying(true);
        try {
            const r = await axios.post(`${API}/coach/packs/backfill-expiry/apply`,
                { confirm: true }, { withCredentials: true });
            setDone(r.data);
            setPreview(null);
            toast.success(`Expiry applied to ${r.data.updated} pack(s)`, {
                description: `${r.data.expired_now} became expired (bought >3 months ago).`,
            });
        } catch (e) {
            toast.error("Apply failed", { description: e?.response?.data?.detail });
        } finally {
            setApplying(false);
        }
    };

    return (
        <section
            data-testid="pack-expiry-backfill-card"
            className="border border-zinc-800 rounded-lg p-5 bg-zinc-950"
        >
            <div className="flex items-start gap-3">
                <CalendarClock className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white">Apply expiry to existing packs</h3>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                        Stamps a <strong>3-month-from-purchase</strong> expiry onto already-sold packs that
                        currently have none. Preview first — packs bought over 3 months ago will expire
                        immediately.
                    </p>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    type="button"
                    data-testid="backfill-preview-btn"
                    onClick={loadPreview}
                    disabled={loading}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase px-4 py-2 border border-zinc-700 text-zinc-200 hover:border-zinc-500 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {preview ? "Refresh preview" : "Preview"}
                </button>
                {preview && preview.total > 0 && (
                    <button
                        type="button"
                        data-testid="backfill-apply-btn"
                        onClick={apply}
                        disabled={applying}
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase px-4 py-2 bg-brand-red text-white hover:bg-brand-red/80 disabled:opacity-50"
                    >
                        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        Apply to {preview.total} pack{preview.total === 1 ? "" : "s"}
                    </button>
                )}
            </div>

            {preview && (
                <div className="mt-4 text-xs text-zinc-300 space-y-1" data-testid="backfill-preview-result">
                    {preview.total === 0 ? (
                        <p className="text-emerald-300">All packs already have an expiry — nothing to do.</p>
                    ) : (
                        <>
                            <p><span className="text-white font-semibold">{preview.total}</span> pack(s) with no expiry would be updated:</p>
                            <p className="text-zinc-400">• {preview.would_stay_active} stay active (expiry in the future)</p>
                            <p className="text-amber-300 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {preview.would_expire_now} become EXPIRED now (bought &gt;3 months ago)
                            </p>
                            {preview.skipped_no_date > 0 && (
                                <p className="text-zinc-500">• {preview.skipped_no_date} skipped (no purchase date on record)</p>
                            )}
                        </>
                    )}
                </div>
            )}

            {done && (
                <p className="mt-4 text-xs text-emerald-300" data-testid="backfill-done">
                    Done — updated {done.updated} pack(s), {done.expired_now} now expired.
                </p>
            )}
        </section>
    );
}
